import { GridCellProps } from '@progress/kendo-react-grid';
import { FC, useCallback, useMemo } from 'react';
import GridCellBase from 'src/components/GridCellBase';
import TextInput from 'src/components/TextInput';
import { isButtonEnabled } from 'src/components/design-system/Button';
import useDelayedInputOnchange from 'src/hooks/useDelayedInputOnchange';
import { round } from 'src/utils/number';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from '../InvoiceDetailsContext';
import OcrIndicator from '../split_line_item/OcrIndicator';

const DescriptionTextField: FC<GridCellProps> = (props) => {
  const { dataItem } = props;
  const isInvoiceEditDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceEditDisabled
  );
  const getMatchingOcrLineItem = useContextSelector(
    InvoiceContext,
    (value) => value.getMatchingOcrLineItem
  );
  const handleHoverOcrField = useContextSelector(
    InvoiceContext,
    (value) => value.handleHoverOcrField
  );
  const initialInvoice = useContextSelector(InvoiceContext, (value) => value.initialInvoice);

  const error = useMemo(() => dataItem?.errors?.description, [dataItem?.errors?.description]);
  const { handleChange, value } = useDelayedInputOnchange(props, true);

  const matchLineItem = getMatchingOcrLineItem(dataItem.receiptLineItemId);
  const ocrConfidence = matchLineItem.reduce(
    (acc, item) => (acc += parseFloat(item?.item?.itemDescription?.confidence || '0')),
    0
  );
  const averageOcrConfidence = round(ocrConfidence / matchLineItem.length, 2);
  const isEdited = useMemo(
    () =>
      matchLineItem[0]?.item?.itemDescription?.value?.toLowerCase()?.trim() !==
      value.toLowerCase()?.trim(),
    [matchLineItem, value]
  );

  const matchItemIndex = matchLineItem.map(
    (item) => Number(item.item?.itemSequenceNumber?.value) - 1
  );

  const handleHover = useCallback(() => {
    handleHoverOcrField('itemDescription', matchItemIndex);
  }, [handleHoverOcrField, matchItemIndex]);

  const tooltip = value.replace('\n', '');

  const showOcrIndicator = useMemo(
    () =>
      initialInvoice?.ocrGenerated &&
      averageOcrConfidence !== 0 &&
      matchLineItem.length > 0 &&
      Number(dataItem?.poQty) === 0 &&
      value.trim().length > 0,
    [
      initialInvoice?.ocrGenerated,
      averageOcrConfidence,
      matchLineItem.length,
      dataItem?.poQty,
      value,
    ]
  );

  return (
    <GridCellBase {...props} className={!error.length ? '' : 'bg-errorcell'} disableToolTip>
      <div className="flex flex-row items-center justify-between gap-1">
        <div className="truncate" data-tooltip-id={'global-tooltip'} data-tooltip-content={tooltip}>
          {Number(dataItem?.poQty) === 0 &&
          isButtonEnabled(dataItem.canDelete) &&
          !isInvoiceEditDisabled ? (
            <TextInput
              type={'text'}
              value={value}
              onChange={handleChange}
              valid={!error?.length}
              onFocus={handleHover}
              ocr-data={`itemDescription:${matchItemIndex}`}
            />
          ) : (
            <span>{value}</span>
          )}
        </div>
        {showOcrIndicator && (
          <OcrIndicator
            isEdited={isEdited}
            ocrConfidence={`${averageOcrConfidence}%`}
            onHover={handleHover}
          />
        )}
      </div>
    </GridCellBase>
  );
};

export default DescriptionTextField;
